import { Link } from '@tanstack/react-router';
import { ArrowRight, Heart, MapPin, Sparkles, Users } from 'lucide-react';
import { MarketingShell } from './MarketingShell';
import { useCms, cmsText, cmsList, cmsRich } from '../../api/cms';
import { SINGLE_FALLBACKS, LIST_FALLBACKS } from '../../content/fallbacks';

// Icons stay fixed in code and pair with the CMS-editable `about.values` list
// items by position (see content/registry.ts note).
const VALUE_ICONS = [Heart, Sparkles, Users];

export function About() {
  const { data: cms } = useCms();
  const intro = SINGLE_FALLBACKS['about.intro']!;
  const values = cmsList(cms, 'about.values', LIST_FALLBACKS['about.values']!);
  const cta = SINGLE_FALLBACKS['about.cta']!;

  return (
    <MarketingShell>
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-10">
        <p className="text-xs font-semibold uppercase tracking-wider text-brand">{cmsText(cms, 'about.intro', 'eyebrow', intro.eyebrow!)}</p>
        <h1 className="mt-3 text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 leading-[1.05]">
          {cmsText(cms, 'about.intro', 'headline', intro.headline!)}
        </h1>
        <div className="mt-6 space-y-4 text-lg text-zinc-600 dark:text-zinc-300 leading-relaxed">
          <p>{cmsRich(cmsText(cms, 'about.intro', 'body1', intro.body1!))}</p>
          <p>{cmsText(cms, 'about.intro', 'body2', intro.body2!)}</p>
          <p>{cmsText(cms, 'about.intro', 'body3', intro.body3!)}</p>
        </div>

        {/* Registered identity. Reads `company.details.registration` — the same single
            source as the footer and the Company page's details table, so an admin edit
            updates every surface at once. /about is platform-only (reseller hosts
            redirect away), so ITCity's licence cannot leak onto a white-label site. */}
        <dl className="mt-8 pt-6 border-t border-zinc-200 dark:border-zinc-800 text-sm">
          <dt className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
            Registered company
          </dt>
          <dd className="mt-1 text-zinc-700 dark:text-zinc-200">
            {cmsText(cms, 'company.details', 'legalName', SINGLE_FALLBACKS['company.details']!.legalName!)}
            {' — '}
            {cmsText(cms, 'company.details', 'registration', SINGLE_FALLBACKS['company.details']!.registration!)}
          </dd>
        </dl>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 grid sm:grid-cols-3 gap-4">
        {values.map((v, i) => {
          const Icon = VALUE_ICONS[i] ?? VALUE_ICONS[VALUE_ICONS.length - 1]!;
          return (
            <div key={i} className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 p-6">
              <span className="w-10 h-10 grid place-items-center rounded-lg bg-brand-subtle dark:bg-brand/15 text-brand dark:text-brand-dm">
                <Icon className="w-5 h-5" />
              </span>
              <h2 className="mt-4 font-semibold text-zinc-950 dark:text-zinc-50">{v.title}</h2>
              <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed">{v.body}</p>
            </div>
          );
        })}
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-20">
        <div className="rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50/70 dark:bg-zinc-900/40 p-8 sm:p-10">
          <div className="flex items-center gap-2 text-sm text-zinc-500 dark:text-zinc-400">
            <MapPin className="w-4 h-4 text-brand dark:text-brand-dm" /> {cmsText(cms, 'about.cta', 'locationTag', cta.locationTag!)}
          </div>
          <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 max-w-xl">
            {cmsText(cms, 'about.cta', 'headline', cta.headline!)}
          </h2>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link to="/signup" className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-brand hover:bg-brand/90 transition">
              Start free <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/become-a-reseller" className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium border border-zinc-300 dark:border-zinc-700 hover:bg-white dark:hover:bg-zinc-800 transition">
              Become a reseller
            </Link>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
